import { isDemoContentAllowed } from "./previewGuard";
import {
    MystudioCollection,
    MystudioContentItem,
    MystudioCursorPage,
    MystudioGalleryPost,
    MystudioGalleryPostPage,
    MystudioGallerySection,
    MystudioHome,
    MystudioInquiry,
    MystudioNotification,
    MystudioPhotoAsset,
    MystudioPhotoAssetPage,
    MystudioPhotoDelivery,
    MystudioProduct,
    MystudioSchedule,
    MystudioThemeCatalog,
} from "./types";

const DEMO_ORIGINAL_IMAGES = [
    "/static/img/demo/customer/demo-first-birthday-studio.jpg",
    "/static/img/demo/customer/demo-family-garden-studio.jpg",
    "/static/img/demo/customer/demo-maternity-studio.jpg",
];

const DEMO_THEME_IMAGES = [
    "/static/img/themes/theme-baby-crown.jpg",
    "/static/img/themes/theme-family-garden.jpg",
    "/static/img/themes/theme-maternity.jpg",
    "/static/img/themes/theme-birthday-pink.jpg",
    "/static/img/themes/theme-pure-window.jpg",
    "/static/img/themes/theme-bunny-baby.jpg",
    "/static/img/themes/theme-playful-baby.jpg",
];

const DEMO_PRODUCT_IMAGES = [
    "/static/img/products/dummy/product-album-linen-studio.webp",
    "/static/img/products/dummy/product-frame-oak-studio.webp",
    "/static/img/products/dummy/product-album-slipcase-studio.webp",
    "/static/img/products/dummy/product-frame-white-studio.webp",
    "/static/img/products/dummy/product-album-open-studio.webp",
];

export const DEMO_PHOTO_ASSETS: MystudioPhotoAsset[] = DEMO_ORIGINAL_IMAGES.map((url, index) => ({
    id: `demo-original-${index + 1}`,
    url,
    thumbnailUrl: url,
    downloadUrl: url,
    fileName: `demo-studio-photo-${String(index + 1).padStart(2, "0")}.jpg`,
    alt: [
        "데모 첫돌 스튜디오 촬영 사진",
        "데모 가족 정원 스튜디오 촬영 사진",
        "데모 만삭 스튜디오 촬영 사진",
    ][index],
    title: ["첫돌 본촬영", "우리 가족사진", "만삭 기념촬영"][index],
    category: ["첫돌", "가족", "만삭"][index],
    officeName: "럽맘 스튜디오",
    uploadedAt: `2026-08-${String(20 - index).padStart(2, "0")}T10:00:00+09:00`,
    width: 1536,
    height: 1024,
}));

export const DEMO_CONTENT_ITEMS: MystudioContentItem[] = [
    {
        id: "demo-event-family-month",
        type: "event",
        title: "[DEMO] 가족사진 촬영 주간",
        summary: "가족이 함께하는 자연스러운 정원 콘셉트 촬영을 체험해 보세요.",
        content: "이 화면은 고객 앱 기능 확인을 위한 데모 이벤트입니다. 실제 예약이나 결제는 진행되지 않습니다.",
        imageUrl: DEMO_ORIGINAL_IMAGES[1],
        publishedAt: "2026-08-24T09:00:00+09:00",
        expiresAt: "2026-12-31T23:59:59+09:00",
        cta: { action: "OPEN_SCHEDULE", label: "촬영 일정 보기" },
    },
    {
        id: "demo-promotion-album",
        type: "promotion",
        title: "[DEMO] 프리미엄 앨범 업그레이드",
        summary: "촬영 사진을 오래 간직할 수 있는 린넨 앨범 구성을 확인해 보세요.",
        content: "데모용 상품 안내입니다. 실제 상품 가격과 구성은 스튜디오 상담을 통해 확정됩니다.",
        imageUrl: DEMO_PRODUCT_IMAGES[0],
        publishedAt: "2026-08-22T09:00:00+09:00",
        expiresAt: "2026-12-31T23:59:59+09:00",
        cta: { action: "OPEN_PRODUCTS", label: "상품 둘러보기" },
    },
    {
        id: "demo-notice-original-download",
        type: "notice",
        title: "[DEMO] 원본사진 다운로드 안내",
        summary: "원본사진은 보관 만료일 전에 내려받아 주세요.",
        content: "나의 사진 메뉴에서 촬영별 원본사진과 보관 기간을 확인할 수 있습니다. 현재 표시되는 사진과 날짜는 기능 확인용 데모 데이터입니다.",
        imageUrl: DEMO_ORIGINAL_IMAGES[0],
        publishedAt: "2026-08-25T10:00:00+09:00",
        deepLink: "/mypage/photos",
    },
    {
        id: "demo-notice-shoot-preparation",
        type: "notice",
        title: "[DEMO] 촬영 전 준비사항을 확인해 주세요",
        summary: "의상, 수유, 주차 안내를 촬영 하루 전 확인해 주세요.",
        content: "촬영 10분 전까지 도착해 주세요. 아기 촬영은 평소 좋아하는 간식과 장난감을 준비하면 자연스러운 촬영에 도움이 됩니다.",
        imageUrl: DEMO_ORIGINAL_IMAGES[1],
        publishedAt: "2026-08-23T10:00:00+09:00",
        deepLink: "/tabs/schedules",
    },
    {
        id: "demo-notice-product-deadline",
        type: "notice",
        title: "[DEMO] 상품 제작사진 선택 기간 안내",
        summary: "앨범과 액자 제작사진은 신청 기한 안에 선택해 주세요.",
        content: "제품신청 메뉴에서 상품별 필요 사진 수와 진행 상태를 확인할 수 있습니다. 데모 환경에서는 제출이 실제 제작으로 연결되지 않습니다.",
        imageUrl: DEMO_PRODUCT_IMAGES[2],
        publishedAt: "2026-08-21T10:00:00+09:00",
        deepLink: "/tabs/products",
    },
    {
        id: "demo-faq-photo-retention",
        type: "faq",
        title: "원본사진은 언제까지 보관되나요?",
        summary: "촬영별 보관 만료일은 나의 사진에서 확인할 수 있어요.",
        content: "촬영별 원본 보관 기간이 다를 수 있으므로 나의 사진 메뉴에 표시된 날짜를 기준으로 확인해 주세요.",
        publishedAt: "2026-08-20T10:00:00+09:00",
    },
    {
        id: "demo-faq-theme-change",
        type: "faq",
        title: "선택한 촬영 테마를 바꿀 수 있나요?",
        summary: "테마 확정 전에는 촬영테마 메뉴에서 다시 선택할 수 있어요.",
        content: "테마 확정 또는 촬영 준비가 시작된 뒤에는 담당 스튜디오에 문의해 주세요.",
        publishedAt: "2026-08-19T10:00:00+09:00",
    },
    {
        id: "demo-faq-product-process",
        type: "faq",
        title: "앨범과 액자는 어떻게 신청하나요?",
        summary: "제품신청에서 상품을 선택하고 필요한 사진을 담아 제출해 주세요.",
        content: "상품별 필요 사진 수를 채운 뒤 배송지와 옵션을 확인하면 신청할 수 있습니다.",
        publishedAt: "2026-08-18T10:00:00+09:00",
    },
];

export const DEMO_INQUIRIES: MystudioInquiry[] = [
    {
        id: "demo-inquiry-1",
        category: "촬영 준비",
        title: "[DEMO] 촬영 당일 준비물을 알려주세요",
        content: "아기 촬영 당일에 꼭 준비해야 할 물품이 궁금해요.",
        status: "answered",
        createdAt: "2026-08-20T14:30:00+09:00",
        answeredAt: "2026-08-20T16:10:00+09:00",
        answer: "평소 좋아하는 간식과 장난감, 여벌 옷을 준비해 주세요. 예약 시간 10분 전 도착을 권장합니다.",
        unreadReplyCount: 1,
        messages: [
            { id: "demo-inquiry-1-customer", senderType: "CUSTOMER", body: "아기 촬영 당일에 꼭 준비해야 할 물품이 궁금해요.", createdAt: "2026-08-20T14:30:00+09:00" },
            { id: "demo-inquiry-1-staff", senderType: "STAFF", body: "평소 좋아하는 간식과 장난감, 여벌 옷을 준비해 주세요. 예약 시간 10분 전 도착을 권장합니다.", createdAt: "2026-08-20T16:10:00+09:00" },
        ],
        attachments: [],
    },
    {
        id: "demo-inquiry-2",
        category: "상품 제작",
        title: "[DEMO] 액자 옵션 변경 문의",
        content: "오크 프레임을 화이트 프레임으로 변경할 수 있을까요?",
        status: "in_progress",
        createdAt: "2026-08-24T11:00:00+09:00",
        unreadReplyCount: 0,
        messages: [
            { id: "demo-inquiry-2-customer", senderType: "CUSTOMER", body: "오크 프레임을 화이트 프레임으로 변경할 수 있을까요?", createdAt: "2026-08-24T11:00:00+09:00" },
        ],
        attachments: [],
    },
];

export const DEMO_NOTIFICATIONS: MystudioNotification[] = [
    {
        id: "demo-notification-1",
        category: "사진",
        title: "[DEMO] 촬영 사진이 도착했어요",
        body: "첫돌 본촬영 사진을 나의 사진에서 확인해 보세요.",
        createdAt: "2026-08-25T09:30:00+09:00",
        deepLink: "/mypage/photos",
        relatedResourceId: "demo-delivery-1",
    },
    {
        id: "demo-notification-2",
        category: "일정",
        title: "[DEMO] 촬영 일정이 가까워졌어요",
        body: "촬영 전 준비사항과 선택한 테마를 확인해 주세요.",
        createdAt: "2026-08-24T18:00:00+09:00",
        deepLink: "/tabs/schedules",
    },
    {
        id: "demo-notification-3",
        category: "상품",
        title: "[DEMO] 제작사진 선택이 필요해요",
        body: "앨범 제작에 사용할 사진을 선택해 주세요.",
        createdAt: "2026-08-23T13:20:00+09:00",
        readAt: "2026-08-23T14:00:00+09:00",
        deepLink: "/tabs/products",
    },
];

const galleryCategory = (id: string, name: string, coverImageUrl: string) => ({
    id,
    name,
    description: `${name} 촬영 포트폴리오`,
    coverImageUrl,
});

const galleryPhotos = (postId: string, offset: number) => DEMO_PHOTO_ASSETS.map((asset, index) => ({
    id: `${postId}-photo-${index + 1}`,
    imageUrl: DEMO_ORIGINAL_IMAGES[(index + offset) % DEMO_ORIGINAL_IMAGES.length],
    thumbnailUrl: DEMO_ORIGINAL_IMAGES[(index + offset) % DEMO_ORIGINAL_IMAGES.length],
    altText: asset.alt,
    sortOrder: index,
    uploadedAt: asset.uploadedAt,
}));

export const DEMO_GALLERY_POSTS: MystudioGalleryPost[] = [
    {
        id: "demo-gallery-first-birthday",
        title: "우리 아기의 따뜻한 첫돌 기록",
        summary: "아이의 표정과 한복의 섬세한 질감을 담은 데모 촬영 이야기입니다.",
        authorDisplayName: "럽맘 스튜디오 에디터",
        category: galleryCategory("demo-baby", "베이비·첫돌", DEMO_ORIGINAL_IMAGES[0]),
        officeName: "럽맘 스튜디오",
        sortOrder: 1,
        photoCount: 3,
        viewCount: 128,
        coverPhoto: galleryPhotos("demo-gallery-first-birthday", 0)[0],
        photos: galleryPhotos("demo-gallery-first-birthday", 0),
        createdAt: "2026-08-24T12:00:00+09:00",
        publishedAt: "2026-08-24T12:00:00+09:00",
    },
    {
        id: "demo-gallery-family-garden",
        title: "햇살 좋은 날의 가족사진",
        summary: "자연스러운 움직임과 웃음을 담은 데모 가족 촬영 이야기입니다.",
        authorDisplayName: "럽맘 스튜디오 에디터",
        category: galleryCategory("demo-family", "가족사진", DEMO_ORIGINAL_IMAGES[1]),
        officeName: "럽맘 스튜디오",
        sortOrder: 2,
        photoCount: 3,
        viewCount: 96,
        coverPhoto: galleryPhotos("demo-gallery-family-garden", 1)[0],
        photos: galleryPhotos("demo-gallery-family-garden", 1),
        createdAt: "2026-08-22T12:00:00+09:00",
        publishedAt: "2026-08-22T12:00:00+09:00",
    },
    {
        id: "demo-gallery-maternity",
        title: "기다림을 담은 만삭사진",
        summary: "차분한 빛과 부드러운 색감으로 완성한 데모 만삭 촬영 이야기입니다.",
        authorDisplayName: "럽맘 스튜디오 에디터",
        category: galleryCategory("demo-family", "가족사진", DEMO_ORIGINAL_IMAGES[2]),
        officeName: "럽맘 스튜디오",
        sortOrder: 3,
        photoCount: 3,
        viewCount: 74,
        coverPhoto: galleryPhotos("demo-gallery-maternity", 2)[0],
        photos: galleryPhotos("demo-gallery-maternity", 2),
        createdAt: "2026-08-20T12:00:00+09:00",
        publishedAt: "2026-08-20T12:00:00+09:00",
    },
];

export const DEMO_GALLERY_SECTIONS: MystudioGallerySection[] = [
    {
        id: "demo-baby",
        name: "베이비·첫돌",
        description: "아이의 오늘을 따뜻하게 기록한 촬영 이야기",
        coverImageUrl: DEMO_ORIGINAL_IMAGES[0],
        sortOrder: 1,
        items: DEMO_GALLERY_POSTS.filter((post) => post.category.id === "demo-baby"),
        total: DEMO_GALLERY_POSTS.filter((post) => post.category.id === "demo-baby").length,
    },
    {
        id: "demo-family",
        name: "가족사진",
        description: "우리 가족의 자연스러운 순간을 담은 촬영 이야기",
        coverImageUrl: DEMO_ORIGINAL_IMAGES[1],
        sortOrder: 2,
        items: DEMO_GALLERY_POSTS.filter((post) => post.category.id === "demo-family"),
        total: DEMO_GALLERY_POSTS.filter((post) => post.category.id === "demo-family").length,
    },
];

function useDemoContent(): boolean {
    return isDemoContentAllowed();
}

function withDemoScheduleImages(items: MystudioSchedule[]): MystudioSchedule[] {
    return items.map((schedule, index) => ({
        ...schedule,
        thumbnailUrl: schedule.thumbnailUrl || DEMO_ORIGINAL_IMAGES[index % DEMO_ORIGINAL_IMAGES.length],
    }));
}

export function enrichDemoSchedules(
    collection: MystudioCollection<MystudioSchedule>,
): MystudioCollection<MystudioSchedule> {
    if (!useDemoContent()) return collection;
    return { ...collection, items: withDemoScheduleImages(collection.items) };
}

export function enrichDemoProducts(
    collection: MystudioCollection<MystudioProduct>,
): MystudioCollection<MystudioProduct> {
    if (!useDemoContent()) return collection;
    return {
        ...collection,
        items: collection.items.map((product, index) => ({
            ...product,
            thumbnailUrl: product.thumbnailUrl || DEMO_PRODUCT_IMAGES[index % DEMO_PRODUCT_IMAGES.length],
        })),
    };
}

export function enrichDemoThemes(catalog: MystudioThemeCatalog): MystudioThemeCatalog {
    if (!useDemoContent()) return catalog;
    return {
        ...catalog,
        items: catalog.items.map((theme, index) => {
            const imageUrl = theme.thumbnailUrl
                || theme.images?.[0]?.url
                || DEMO_THEME_IMAGES[index % DEMO_THEME_IMAGES.length];
            return {
                ...theme,
                thumbnailUrl: imageUrl,
                images: theme.images?.length ? theme.images : [{ id: `${theme.id}-demo`, url: imageUrl }],
            };
        }),
    };
}

export function enrichDemoHome(home: MystudioHome): MystudioHome {
    if (!useDemoContent()) return home;
    const products = enrichDemoProducts({ items: home.products, availability: "available" }).items;
    const popularThemes = home.popularThemes.map((theme, index) => {
        const imageUrl = theme.thumbnailUrl
            || theme.images?.[0]?.url
            || DEMO_THEME_IMAGES[index % DEMO_THEME_IMAGES.length];
        return {
            ...theme,
            thumbnailUrl: imageUrl,
            images: theme.images?.length ? theme.images : [{ id: `${theme.id}-demo`, url: imageUrl }],
        };
    });
    const recentInquiries = home.communication?.recentInquiries?.length
        ? home.communication.recentInquiries
        : DEMO_INQUIRIES;
    return {
        ...home,
        upcomingSchedules: withDemoScheduleImages(home.upcomingSchedules),
        completedSchedules: withDemoScheduleImages(home.completedSchedules),
        products,
        reviewEvent: !home.reviewEvent || home.reviewEvent.availability === "unavailable"
            ? {
                id: "demo-review-event",
                availability: "available",
                eligible: true,
                status: "uploaded",
                downloadExpiresAt: "2027-12-31T23:59:59+09:00",
                photos: DEMO_PHOTO_ASSETS,
            }
            : home.reviewEvent,
        reviewPhotos: home.reviewPhotos.length ? home.reviewPhotos : DEMO_PHOTO_ASSETS,
        galleryPosts: home.galleryPosts.length ? home.galleryPosts : DEMO_GALLERY_POSTS,
        gallerySections: home.gallerySections.length ? home.gallerySections : DEMO_GALLERY_SECTIONS,
        events: home.events.length
            ? home.events
            : DEMO_CONTENT_ITEMS.filter((item) => item.type === "event" || item.type === "promotion"),
        notices: home.notices.length
            ? home.notices
            : DEMO_CONTENT_ITEMS.filter((item) => item.type === "notice"),
        faqs: home.faqs.length
            ? home.faqs
            : DEMO_CONTENT_ITEMS.filter((item) => item.type === "faq"),
        communication: {
            ...home.communication,
            recentInquiries,
            unreadReplyCount: home.communication?.unreadReplyCount || 1,
        },
        popularThemes,
    };
}

export function createDemoGalleryPostPage({
    categoryId,
    sort = "latest",
}: {
    categoryId?: string;
    sort?: string;
} = {}): MystudioGalleryPostPage | undefined {
    if (!useDemoContent()) return undefined;
    const filtered = categoryId
        ? DEMO_GALLERY_POSTS.filter((post) => post.category.id === categoryId)
        : [...DEMO_GALLERY_POSTS];
    const items = filtered.sort((left, right) => sort === "popular"
        ? right.viewCount - left.viewCount
        : String(right.publishedAt || "").localeCompare(String(left.publishedAt || "")));
    return { items, page: 1, limit: items.length, total: items.length, hasNext: false };
}

export function getDemoGalleryPost(postId?: string): MystudioGalleryPost | undefined {
    if (!useDemoContent() || !postId) return undefined;
    return DEMO_GALLERY_POSTS.find((post) => post.id === postId);
}

export function createDemoGallerySections(): MystudioGallerySection[] | undefined {
    return useDemoContent() ? DEMO_GALLERY_SECTIONS : undefined;
}

export function createDemoPublicContent(): MystudioCollection<MystudioContentItem> | undefined {
    if (!useDemoContent()) return undefined;
    return {
        items: DEMO_CONTENT_ITEMS,
        availability: "available",
        total: DEMO_CONTENT_ITEMS.length,
    };
}

export function enrichDemoPhotoDeliveries(
    collection: MystudioCollection<MystudioPhotoDelivery>,
): MystudioCollection<MystudioPhotoDelivery> {
    if (!useDemoContent()) return collection;
    const titles = ["첫돌 본촬영", "가족사진", "만삭 기념촬영", "성장 기록촬영", "기념일 촬영"];
    const sourceItems = collection.items.length ? collection.items : [
        { id: "demo-delivery-1", scheduleId: "demo-schedule-1", scheduleDate: "2026-08-18", photoCount: 128 },
        { id: "demo-delivery-2", scheduleId: "demo-schedule-2", scheduleDate: "2026-07-12", photoCount: 96 },
        { id: "demo-delivery-3", scheduleId: "demo-schedule-3", scheduleDate: "2026-06-04", photoCount: 84 },
    ];
    return {
        ...collection,
        availability: "available",
        reasonCode: undefined,
        total: sourceItems.length,
        items: sourceItems.map((delivery, index) => ({
            ...delivery,
            title: titles[index % titles.length],
            officeName: "럽맘 스튜디오",
            type: delivery.type || "본촬영",
            status: delivery.status || "READY",
            photoCount: delivery.photoCount || 60 + index * 12,
            expiresAt: "2027-12-31T23:59:59+09:00",
            previewImages: [0, 1, 2].map((offset) => DEMO_PHOTO_ASSETS[(index + offset) % DEMO_PHOTO_ASSETS.length]),
            demo: true,
        })),
    };
}

export function createDemoPhotoAssetPage(deliveryId: string): MystudioPhotoAssetPage | undefined {
    if (!useDemoContent() || !deliveryId) return undefined;
    const offset = Math.abs(deliveryId.split("").reduce((sum, character) => sum + character.charCodeAt(0), 0));
    const items = [0, 1, 2, 0, 1, 2].map((imageIndex, index) => {
        const source = DEMO_PHOTO_ASSETS[(imageIndex + offset) % DEMO_PHOTO_ASSETS.length];
        return {
            ...source,
            id: `${deliveryId}-demo-photo-${index + 1}`,
            fileName: `${deliveryId}-demo-${String(index + 1).padStart(2, "0")}.jpg`,
        };
    });
    return {
        items,
        availability: "available",
        total: items.length,
        page: 1,
        pageSize: items.length,
        hasNext: false,
    };
}

export function createDemoInquiryPage(): MystudioCursorPage<MystudioInquiry> | undefined {
    if (!useDemoContent()) return undefined;
    return {
        items: DEMO_INQUIRIES,
        total: DEMO_INQUIRIES.length,
        nextCursor: null,
        availability: "available",
    };
}

export function getDemoInquiry(requestId?: string): MystudioInquiry | undefined {
    if (!useDemoContent() || !requestId) return undefined;
    return DEMO_INQUIRIES.find((inquiry) => inquiry.id === requestId);
}

export function createDemoNotificationPage(): MystudioCursorPage<MystudioNotification> | undefined {
    if (!useDemoContent()) return undefined;
    return {
        items: DEMO_NOTIFICATIONS,
        total: DEMO_NOTIFICATIONS.length,
        unreadCount: DEMO_NOTIFICATIONS.filter((notification) => !notification.readAt).length,
        nextCursor: null,
        availability: "available",
    };
}

export function isDemoResourceId(id?: string): boolean {
    return Boolean(useDemoContent() && String(id || "").startsWith("demo-"));
}
